// index.tsx - Navigation Component with createStyles function
import React from 'react';
import { Sun, Moon, Menu, X } from 'lucide-react';

interface NavigationProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
  isDarkMode: boolean;
  setIsDarkMode: (value: boolean) => void;
  setActiveView: (view: string) => void;
  styles: any;
}

// Add the createStyles function that was missing
export const createStyles = (isDarkMode: boolean) => ({
  container: {
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  nav: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${isDarkMode ? '#333' : '#eee'}`,
    zIndex: 50,
    transition: 'all 0.3s',
  },
  navContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '1rem 2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    letterSpacing: '0.05em',
    cursor: 'pointer',
    color: isDarkMode ? '#fff' : '#000',
  },
  navLinks: {
    display: 'flex',
    gap: '2rem',
    alignItems: 'center',
  },
  navLink: {
    background: 'none',
    border: 'none',
    color: isDarkMode ? '#fff' : '#000',
    cursor: 'pointer',
    fontSize: '1rem',
    transition: 'opacity 0.3s',
    opacity: 0.8,
    ':hover': {
      opacity: 1,
    },
  },
});

export const Navigation: React.FC<NavigationProps> = ({ 
  isMenuOpen, 
  setIsMenuOpen, 
  isDarkMode, 
  setIsDarkMode, 
  setActiveView,
  styles
}) => {
  return (
    <nav style={styles.nav}>
      <div style={styles.navContent}>
        <div style={styles.logo} onClick={() => setActiveView('home')}>
          KENT CONSTRUCTION
          <span style={{ fontSize: '0.7rem', display: 'block', opacity: 0.7, letterSpacing: '0.1em' }}>
            CSLB# 1034839
          </span>
        </div>
        
        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
          <div style={{ ...styles.navLinks, display: window.innerWidth > 768 ? 'flex' : 'none' }}>
            <button style={styles.navLink} onClick={() => setActiveView('home')}>Home</button>
            <button style={styles.navLink} onClick={() => setActiveView('about')}>About</button>
            <button style={styles.navLink} onClick={() => setActiveView('projects')}>Projects</button>
            <button style={styles.navLink} onClick={() => setActiveView('contact')}>Contact</button>
          </div>
          
          <button 
            onClick={() => setIsDarkMode(!isDarkMode)}
            style={{ ...styles.navLink, padding: '0.5rem' }}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          
          <button 
            style={{ ...styles.navLink, display: window.innerWidth <= 768 ? 'block' : 'none' }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div style={{
          position: 'fixed',
          top: '60px',
          left: 0,
          right: 0,
          backgroundColor: isDarkMode ? '#000' : '#fff',
          borderBottom: `1px solid ${isDarkMode ? '#333' : '#eee'}`,
          padding: '1rem',
          zIndex: 40
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button style={styles.navLink} onClick={() => { setActiveView('home'); setIsMenuOpen(false); }}>Home</button>
            <button style={styles.navLink} onClick={() => { setActiveView('about'); setIsMenuOpen(false); }}>About</button>
            <button style={styles.navLink} onClick={() => { setActiveView('projects'); setIsMenuOpen(false); }}>Projects</button>
            <button style={styles.navLink} onClick={() => { setActiveView('contact'); setIsMenuOpen(false); }}>Contact</button>
          </div>
        </div>
      )}
    </nav>
  );
};